import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Modal, Row } from "react-bootstrap";
import { Direction, ITravelSegment } from "../Models";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { IAvailableOption, ITableBooking, getApiCallState, useStateSelector } from "./Model";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { bookTableForMeal, cancelTableBooking, loadAvailableTimesForSegment, loadOptions, updateOptions, updateOptionsOnTableBooking } from "./Actions";
import { IKrogenSlot, getKrogenProductCodes, getKrogenSlotForProductCode, getSlotStartAndEndTime } from "../TravelDetails/KrogenOptionsHelpers";
import { isTest } from "../S3BookingApp";
import { NumberSelector } from "../Components/NumberSelector";
import { Price } from "../Components/Price";
import SvgInfoCircleFill from "../../Shared/Icons/bootstrap/InfoCircleFill";
import SvgExclamationTriangleFill from "../../Shared/Icons/bootstrap/ExclamationTriangleFill";
import { formatteraTal } from "../Shared/Utils";
import { clearApiCallState } from "../ApiCalls/Actions";

const strings = getStrings().component.tableBooking;
const orderStrings = getStrings().component.orderSummary;
const commonStrings = getStrings().component.common;
const optionStrings = getStrings().component.options;

const krogenCodes = getKrogenProductCodes();

export const BookMealsModal = (props: { show: boolean, bookingNumber: string, direction: Direction, segment?: ITravelSegment, maxPassengerCount: number, onClosed: (optionsChanged: boolean) => void }) => {

	const dispatch = useDispatch();

	const [changedOptions, setChangedOptions] = useState<{tariffCode: string, count: number, price: number, slot: string, segmentId: string}[]>([]);

	const departure = props.segment?.departure && dayjs(props.segment.departure);
	const arrival = props.segment?.arrival && dayjs(props.segment.arrival);

	const booking = useStateSelector(state => state.manageBooking.booking);

	const apicalls = getApiCallState();
	const updateOptionsState = apicalls.getLoadingState("updateOptions");

	const availableOptions = booking?.availableOptions;
	const krogenOptions = availableOptions?.segments.find(x => x.segment.id === props.segment?.segmentId)?.options?.filter(opt => krogenCodes.includes(opt.code));

	const direction = booking.outbound?.segments?.some(x => x.segmentId === props.segment?.segmentId) ? "outbound" : "inbound";
	const currentOptions = props.segment?.options;

	const optionsBySlot = useMemo(() => getOptionsBySlot(krogenOptions, props.segment), [krogenOptions, props.segment]);


	const enableSubmit = useMemo(() => {
		const slots = optionsBySlot?.map(x => x.slot.slotName);
		if (!slots)
			return false;

		//Kontrollera att vi för varje slot antingen varken har bordsbokning eller tillval, eller att antalet tillval stämmer överrens med antal personer på bordsbokningen
		return slots.every(slot => {
			const tableBooking = booking?.tableBooking?.bookings?.find(x => x.slot === slot && x.segmentId === props.segment?.segmentId);
			const optionCount = changedOptions?.filter(x => x.slot === slot).reduce((sum, curr) => sum+curr.count, 0) || 0;
			return (tableBooking?.persons || 0) === optionCount;
		});


	}, [optionsBySlot, changedOptions, booking?.tableBooking?.bookings])

	const optionCountChanged = (option: IAvailableOption, newCount: number, currentCount: number, slot: string) =>
	{
		console.debug("optionCountChanged", option, newCount, currentCount);

		const temp = [...changedOptions];
		const itemIndex = temp.findIndex(x => x.slot === slot && x.tariffCode === option.tariffCode);

		if (newCount <= currentCount && itemIndex >= 0) //Ta bort om det är samma som tidigare (eller färre, men det ska det inte kunna bli)
			temp.splice(itemIndex, 1);
		else if (newCount > currentCount && itemIndex >= 0) //Uppdatera antal om den redan finns
			temp[itemIndex].count = newCount;
		else if (newCount > currentCount) //Annars lägg till den
			temp.push({ tariffCode: option.tariffCode, count: newCount, price: option.price, slot, segmentId: props.segment.segmentId });

		setChangedOptions(temp);
	};

	const bookTable = (count: number, time: string, date: string, slotName: string, currentBooking?: ITableBooking) => {
		const options = getOptionsForSlotName(slotName);
		const isTemporary = changedOptions.filter(x => x.slot === slotName).length > 0;

		dispatch(bookTableForMeal(props.bookingNumber, direction, props.segment.segmentId, props.segment.serviceName, count, time, date, slotName, isTemporary, currentBooking, options));
	};

	const abortClicked = () => {
		//Avboka alla nygjorda bordsbokningar om vi stänger
		const bookingsToCancel = booking?.tableBooking?.bookings?.filter(x => x.isNew);
		if (bookingsToCancel?.length > 0 && !confirm(strings.mealBookingDialog.confirmCancel))
			return;

		bookingsToCancel?.forEach(x => {
			dispatch(cancelTableBooking(booking.bookingNumber, x.bookingId));
		});
		setChangedOptions([]);
		props.onClosed(false);
	};

	const submit = () => {
		if (booking?.bookingNumber && changedOptions?.length > 0)
		{
			const options = optionsBySlot.map(x => x.slot).flatMap(slot => getOptionsForSlotName(slot.slotName))

			dispatch(updateOptions(booking.bookingNumber, changedOptions));
			dispatch(updateOptionsOnTableBooking(booking.bookingNumber, props.segment.segmentId, props.segment.serviceName, options))
		}
	};

	const closeAndGoToPayment = () => {
		dispatch(clearApiCallState("updateOptions"));
		dispatch(clearApiCallState("bookTableForMeal"));
		props.onClosed(true);
	};

	const getOptionsForSlotName = (slotName: string) => {
		//Befintliga tillval
		const options = currentOptions.filter(opt => krogenCodes.includes(opt.code) && getKrogenSlotForProductCode(opt.code)?.slotName === slotName).map(x => ({ code: x.tariffCode, count: x.count }));

		//Lägg till ändrade
		changedOptions.filter(x => x.slot === slotName).forEach(opt => {
			const idx = options.findIndex(x => x.code == opt.tariffCode);
			if (idx >= 0)
				options[idx].count = opt.count; //changedoptions innehåller det totala antalet
			else
				options.push({ code: opt.tariffCode, count: opt.count });
		});
		return options;
	}

	useEffect(() => {
		if (!availableOptions)
			dispatch(loadOptions(props.bookingNumber));
	}, [availableOptions]);


	return <Modal show={props.show && !!props.segment} centered backdrop="static" size="lg">
		<Modal.Header>
			<Modal.Title><i className="travelicon-KROG" />{strings.mealBookingDialog.title}</Modal.Title>
		</Modal.Header>

		{ updateOptionsState === "loaded" ? <>
			<Modal.Body>
				<h4>{optionStrings.editOptions.optionsUpdatedHeading}</h4>
				<p>{optionStrings.editOptions.optionsUpdatedText}</p>
				{booking?.totalPriceToBePaid > 0 && <p>{optionStrings.editOptions.optionsPaymentText.replace("{totalPriceToBePaid}", formatteraTal(booking.totalPriceToBePaid, null, 2, 0))}</p>}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={closeAndGoToPayment}>{commonStrings.close}</Button>
			</Modal.Footer>
		</> : <>

			<Modal.Body className="book-table-dialog">

				<h4>{props.segment?.from.name} ➔ {props.segment?.to.name}</h4>

				<dl>
					<dt>{orderStrings.departure}</dt>
					<dd>{departure?.format("dddd D MMM, HH:mm")}</dd>
					<dt>{orderStrings.arrival}</dt>
					<dd>{arrival?.format("dddd D MMM, HH:mm")}</dd>
				</dl>

				{ !availableOptions && <Alert variant="info">{strings.mealBookingDialog.optionsLoading}</Alert>}
				{ !optionsBySlot?.length && <Alert variant="warning">{strings.mealBookingDialog.noOptions}</Alert>}

				{ optionsBySlot?.map(x => {

					//Räkna ut totalt antal bokade måltider
					const optionCount = x.options.map(opt => {
						const changedOption = changedOptions.find(y => y.tariffCode === opt.tariffCode);
						const currentOption = currentOptions.find(y => y.tariffCode === opt.tariffCode);

						return {
							tariffCode: opt.tariffCode,
							count: (changedOption ? changedOption.count : currentOption?.count) || 0
						};
					}).reduce((sum, opt) => sum+opt.count, 0);
					const optionsLeft = props.maxPassengerCount - optionCount;

					return <div key={x.slot.slotName} className="option-selector">
						<h4>{strings.slots[x.slot.slotName]}{optionsBySlot.some(x => x.slot.departureDayOffset > 0) ? `, ${dayjs(props.segment.departure).add(x.slot.departureDayOffset, "days").format("D MMM")}`  : null} {isTest() && <Badge variant="light" className="ml-4">{x.slot.startTime}-{x.slot.endTime}  ({optionCount})</Badge>}</h4>

						{ x.options.map(opt => {
							const initialCount = currentOptions?.find(x => x.code === opt.code)?.count || 0;
							const current = changedOptions?.find(x => x.tariffCode === opt.code)?.count || initialCount;
							const max = current + optionsLeft;

							return <React.Fragment key={opt.code}>
								<Row className="mb-1">
									<Col xs={9} lg={10}><label htmlFor={`option_${opt.code}`}>{opt.name} (<Price amount={opt.price} />){isTest() && <Badge variant="light" className="ml-4">{opt.code}</Badge>}</label></Col>
									<Col xs={3} lg={2}><NumberSelector inputId={`option_${opt.code}`} min={initialCount} max={max} initialValue={initialCount} onValueChanged={n => optionCountChanged(opt, n, initialCount, x.slot.slotName)} /></Col>
								</Row>
							</React.Fragment>;
						})}

						<KrogenBokning segment={props.segment} option={x.options[0]}
							count={optionCount} slot={x.slot}
							onBookTable={(time: string, date: string, currentBooking?: ITableBooking) => bookTable(optionCount, time, date, x.slot.slotName, currentBooking)} />
					</div>;
				})}

				{ isTest() && <code>
					<p>currentOptions = {JSON.stringify(currentOptions)}</p>
					<p>changedOptions = {JSON.stringify(changedOptions)}</p>
				</code>}

			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={abortClicked}>{commonStrings.abort}</Button>
				<Button variant="primary" disabled={!enableSubmit || updateOptionsState === "loading"} onClick={submit}>{strings.mealBookingDialog.submitButton}</Button>
			</Modal.Footer>
		</>}
	</Modal>;
};

const KrogenBokning = (props: { segment: ITravelSegment, option: IAvailableOption, count: number, slot: IKrogenSlot, onBookTable: (time: string, date: string, currentBooking?: ITableBooking) => void}) => {

	const dispatch = useDispatch();

	const [showChangeTable, setShowChangeTable] = useState(false);
	const [selectedTime, setSelectedTime] = useState("");
	// const [isBooking, setIsBooking] = useState(false);

	const apiState = getApiCallState();
	const bookTableState = apiState.getLoadingState("bookTableForMeal");
	const cancelTableState = apiState.getLoadingState("cancelTableBooking");

	const isBooking = bookTableState === "loading";
	const isCancelling = cancelTableState === "loading";

	const booking = useStateSelector(state => state.manageBooking.booking);
	const tableBooking = useStateSelector(state => state.manageBooking.booking?.tableBooking);
	const numberOfPassengers = booking.passengers.length;
	const availableTimes = tableBooking?.availableTimes?.hasOwnProperty(props.segment.segmentId) ? tableBooking.availableTimes[props.segment.segmentId].find(x => x.productCode === props.option.code) : null;

	//Inte fler än antal passagerare
	const guestCount = Math.min(numberOfPassengers, props.count);

	const currentBooking = tableBooking?.bookings?.find(x => x.segmentId === props.segment.segmentId && (x.slot === props.slot.slotName || x.type === props.slot.type));
	const currentBookingDate = currentBooking?.date ? dayjs(currentBooking.date) : null;
	const isNightTrain = !dayjs(props.segment.departure).isSame(props.segment.arrival, "day");
	const isPreliminary = !currentBooking || currentBooking.isNew;

	const selectTime = (time: string) => {
		console.debug("selectTime", time, availableTimes.date);
		if (time === currentBooking?.time)
			return;

		setSelectedTime(time);
		// setIsBooking(true);
		props.onBookTable(time, availableTimes.date, currentBooking);
	};

	useEffect(() => {
		if (bookTableState === "loaded")
			setShowChangeTable(false);

		if (bookTableState === "loaded" || bookTableState === "error")
			setSelectedTime("");
	}, [bookTableState])

	useEffect(() => {
		if (guestCount > 0)
			dispatch(loadAvailableTimesForSegment(booking.bookingNumber, props.segment, guestCount, props.option.code, props.slot.startTime, props.slot.endTime, props.slot.departureDayOffset))
	}, [props.segment.segmentId, props.option.code, guestCount]);

	useEffect(() => {
		if (currentBooking && props.count === 0)
			dispatch(cancelTableBooking(booking.bookingNumber, currentBooking.bookingId));
		if (currentBooking?.isNew && props.count !== currentBooking.persons)
			dispatch(cancelTableBooking(booking.bookingNumber, currentBooking.bookingId));
	}, [props.count]);

	if (currentBooking && !showChangeTable && !isCancelling)
		return <p className="text-right">
			{ props.count > 0 && props.count !== currentBooking.persons && <Badge variant="danger" className="mr-2">{strings.mealBookingDialog.wrongNumberOfPeopleWarning}</Badge>}
			{isPreliminary ? strings.mealBookingDialog.tableBookedPrel : strings.mealBookingDialog.tableBooked} <strong>{currentBooking.time}{isNightTrain ? `, ${currentBookingDate.format("D MMM")}` : ""} {numberOfPassengers > 1 && `(${currentBooking.persons} ${currentBooking.persons === 1 ? commonStrings.person : commonStrings.persons})`}</strong>
			<Button variant="link" className="ml-2" onClick={() => setShowChangeTable(true)}>{strings.changeTime}</Button>
		</p>

	if (props.count === 0)
		return <p className="text-right muted"><SvgInfoCircleFill /> {strings.bookingRequired}</p>;

	if (!availableTimes || isCancelling)
		return <p className="text-right muted">{strings.fetchingTables}</p>;

	if (availableTimes?.times.length === 0)
		return <p className="text-right danger"><SvgExclamationTriangleFill /> {strings.noAvailableTimes}</p>;

	return <div className="availableTimes mt-3 mb-3">
		<label>{strings.availableTimes} ({strings.numberOfGuests.toLocaleLowerCase()}: {guestCount})</label><br />
		{availableTimes.times.map(x => <Button variant="outline-primary" size="sm" className={"mr-1 mb-2"} active={currentBooking?.time === x || selectedTime === x} key={x} onClick={() => selectTime(x)}>{x}</Button>)}
		{ isBooking && <div>{isPreliminary ? strings.mealBookingDialog.bookingTablePrel : strings.mealBookingDialog.bookingTable}</div>}
	</div>
};

function getOptionsBySlot(options: IAvailableOption[], segment: ITravelSegment)
{
	return options?.reduce((acc, opt) => {
		const slot = getKrogenSlotForProductCode(opt.code);
		if (!slot)
			return acc;

		//Filtrera bort om man inte kan utnyttja tiden, t.ex. frukost om man åker senare än 10 (eller vad det blir)
		const slotTimes = getSlotStartAndEndTime(slot, segment.departure, segment.arrival);
		if (!slotTimes)
			return acc;

		let item = acc.find(x => x.slot.slotName === slot.slotName);
		if (item)
			item.options.push(opt);
		else
			acc.push({slot, options: [opt]});
		return acc;
	}, ([] as {slot: IKrogenSlot, options: IAvailableOption[]}[])).sort((a, b) => {
		if (a.slot.departureDayOffset > b.slot.departureDayOffset)
			return 1;
		if (a.slot.departureDayOffset < b.slot.departureDayOffset)
			return -1;

			//Funkar eftersom tider alltid skrivs som HH:mm -- då går de att sortera som strängar
		if (a.slot.startTime > b.slot.startTime)
			return 1;
		if (a.slot.startTime < b.slot.startTime)
			return -1;


		return 0;
	})
}